/**
 * Google Maps API Client
 * 
 * Communicates with backend proxy for Google Maps services
 * This keeps the API key secure and allows for caching/rate limiting
 */

import config from '../config/config';

// Generate a unique session token for billing optimization
// Session tokens group multiple API calls (autocomplete + details) into a single billing session
export const generateSessionToken = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Search for places using Google Places Autocomplete
 * 
 * @param {string} input - Search query
 * @param {string} sessionToken - Session token for billing optimization
 * @param {string} types - Place types to filter (optional, e.g., "establishment", "geocode", or "" for all)
 * @returns {Promise<Array>} Array of place predictions
 * 
 * @example
 * const suggestions = await searchPlaces("Moscone Center", sessionToken);
 * // Returns: [{ placeId: "ChI...", description: "Moscone Center...", ... }]
 */
export const searchPlaces = async (input, sessionToken, types = '') => {
  try {
    // Get auth token
    const authToken = localStorage.getItem('accessToken');
    if (!authToken) {
      console.error('❌ No access token found in localStorage');
      throw new Error('Not authenticated');
    }
    
    // Build URL
    const url = new URL(`${config.API_BASE_URL}/em/v1/places/autocomplete`);
    url.searchParams.append('input', input);
    if (sessionToken) {
      url.searchParams.append('sessionToken', sessionToken);
    }
    if (types) {
      url.searchParams.append('types', types);
    }
    
    console.log('🔍 Searching places:', {
      url: url.toString(),
      input: input,
      inputLength: input.length,
      hasSpaces: input.includes(' '),
      encodedInput: encodeURIComponent(input),
      types,
      sessionToken,
      hasToken: !!authToken,
      tokenPreview: authToken.substring(0, 20) + '...'
    });
    
    // Call backend API
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('📥 Response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API Error:', errorData);
      throw new Error(errorData.message || 'Failed to search places');
    }
    
    const data = await response.json();
    console.log('✅ Search results:', data);
    
    if (data.status === 'success') {
      const predictions = data.data.predictions || [];
      console.log(`📊 Received ${predictions.length} predictions from backend`);
      
      // Log additional debug info from backend
      if (data.data.status) {
        console.log(`   Google API Status: ${data.data.status}`);
      }
      if (data.data.count !== undefined) {
        console.log(`   Result count: ${data.data.count}`);
      }
      
      return predictions;
    } else {
      console.error('❌ Backend returned error status:', data.message);
      throw new Error(data.message || 'Failed to search places');
    }
    
  } catch (error) {
    console.error('❌ Error searching places:', error);
    throw error;
  }
};

/**
 * Get detailed information about a specific place
 * 
 * @param {string} placeId - Google Place ID
 * @param {string} sessionToken - Session token (should match the one used in autocomplete)
 * @returns {Promise<Object>} Place details including timezone
 * 
 * @example
 * const details = await getPlaceDetails("ChIJKxjxuaNqj4AR3...", sessionToken);
 * // Returns: { name: "Moscone Center", timezone: "America/Los_Angeles", ... }
 */
export const getPlaceDetails = async (placeId, sessionToken) => {
  try {
    // Get auth token
    const authToken = localStorage.getItem('accessToken');
    if (!authToken) {
      console.error('❌ No access token found in localStorage');
      throw new Error('Not authenticated');
    }
    
    // Build URL
    const url = new URL(`${config.API_BASE_URL}/em/v1/places/details/${placeId}`);
    if (sessionToken) {
      url.searchParams.append('sessionToken', sessionToken);
    }
    
    console.log('📍 Fetching place details:', {
      url: url.toString(),
      placeId,
      hasToken: !!authToken
    });
    
    // Call backend API
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('📥 Response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API Error:', errorData);
      throw new Error(errorData.message || 'Failed to fetch place details');
    }
    
    const data = await response.json();
    console.log('✅ Place details:', data);
    
    if (data.status === 'success') {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch place details');
    }
    
  } catch (error) {
    console.error('❌ Error fetching place details:', error);
    throw error;
  }
};

/**
 * Get timezone for given coordinates
 * 
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<Object>} Timezone information
 * 
 * @example
 * const tz = await getTimezone(37.7842, -122.4016);
 * // Returns: { timezone: "America/Los_Angeles", ... }
 */
export const getTimezone = async (latitude, longitude) => {
  try {
    // Get auth token
    const authToken = localStorage.getItem('accessToken');
    if (!authToken) {
      throw new Error('Not authenticated');
    }
    
    // Build URL
    const url = new URL(`${config.API_BASE_URL}/em/v1/places/timezone`);
    url.searchParams.append('latitude', latitude);
    url.searchParams.append('longitude', longitude);
    
    // Call backend API
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch timezone');
    }
    
    const data = await response.json();
    
    if (data.status === 'success') {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch timezone');
    }
    
  } catch (error) {
    console.error('Error fetching timezone:', error);
    throw error;
  }
};

/**
 * Debounce helper function
 * Delays function execution until after wait milliseconds have elapsed
 * since the last time it was invoked
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} Debounced function
 * 
 * @example
 * const debouncedSearch = debounce(searchPlaces, 300);
 * debouncedSearch("Mosc"); // Waits 300ms before executing
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

